import { getCityLocationList } from '_services/accountsServices/cityLocationService';
import { CrossButton, RouteCustomDropdown, TripRoutingModal } from 'components'
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import toast from 'react-hot-toast';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaCheckCircle, FaMapMarkerAlt, FaClock, FaUser, FaPhone } from 'react-icons/fa'
import {
    GoogleMap,
    Marker,
    InfoWindow,
    DirectionsService,
    DirectionsRenderer,
    Polyline,
} from "@react-google-maps/api";
import { useGoogleMaps } from "components/GoogleMapsProvider/GoogleMapsProvider";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import AddLocationModal from 'modals/AddLocationModal';
import Loading from 'pages/Loading';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// Sortable wrapper component for dropdown items - minimal UI changes
const SortableDropdownItem = ({ id, children, disabled }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id,
        disabled: disabled
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            {...(!disabled ? { ...attributes, ...listeners } : {})}
            className={`${isDragging ? 'dragging' : ''}`}
            title={!disabled ? "Drag to reorder" : ""}
        >
            {children}
        </div>
    );
};

// Generate unique ID for each item
const generateUniqueId = () => `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const initiFrom = {
    from_city: "",
    from_state: "",
    lat: "",
    long: "",
    uniqueId: generateUniqueId(), // Add unique ID
};

const initiTo = {
    to_city: "",
    to_state: "",
    lat: "",
    long: "",
    uniqueId: generateUniqueId(), // Add unique ID
};

const RoutePanningWithoutLrModal = (props) => {
    const { fromLoction, toLoction, setFromLoction, setToLoction, setShow, closeModal, submitByWithoutLr, loading } = props;
    const isLoaded = useGoogleMaps();
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const clustererRef = useRef(null);
    const [directionsResponse, setDirectionsResponse] = useState(null);
    // Initialize with objects that have uniqueId
    const [pickupPoints, setPickupPoints] = useState([{
        ...initiFrom,
        uniqueId: generateUniqueId()
    }]);
    const [dropPoints, setDropPoints] = useState([{
        ...initiTo,
        uniqueId: generateUniqueId()
    }]);

    // Validation states
    const [pickupErrors, setPickupErrors] = useState({});
    const [dropErrors, setDropErrors] = useState({});
    const [openLoction, setOpenLoction] = useState(false)
    const [typeOfDropdown, setTypeOfDropdown] = useState({
        type: "",
        index1: '',
        index: ""
    })
    const [modalZIndex,setModalZIndex] = useState("")
    const containerStyle = {
        width: "100%",
        height: "calc(100% - 84px)",
        minHeight: "422px"
    };

    const defaultCenter = useMemo(() => ({ lat: 20.5937, lng: 78.9629 }), []);
    const defaultZoom = 5;

    const mapOptions = useMemo(
        () => ({
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: true,
            mapTypeControl: true,
            fullscreenControl: true,
        }),
        []
    );



    // Set up sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Shared initial city data for all dropdowns
    const [initialCityData, setInitialCityData] = useState({
        items: [],
        total: 0,
        isLoaded: false,
        isLoading: false
    });

    // Cache for search results to avoid duplicate API calls
    const searchCacheRef = useRef(new Map());

    // Validation functions
    const validatePickupPoint = (point) => {
        if (!point.name || !point.stateName || !point.latitude || !point.longitude) {
            return "Please select a valid pickup location";
        }
        return null;
    };

    const validateDropPoint = (point) => {
        if (!point.name || !point.stateName || !point.latitude || !point.longitude) {
            return "Please select a valid drop location";
        }
        return null;
    };

    // Check if all current pickup points are filled
    const areAllPickupPointsFilled = useMemo(() => {
        return pickupPoints.every(point =>
            point.name && point.stateName && point.latitude && point.longitude
        );
    }, [pickupPoints]);

    // Check if all current drop points are filled
    const areAllDropPointsFilled = useMemo(() => {
        return dropPoints.every(point =>
            point.name && point.stateName && point.latitude && point.longitude
        );
    }, [dropPoints]);

    // Validate all points
    const validateAllPoints = () => {
        const newPickupErrors = {};
        const newDropErrors = {};
        let hasErrors = false;

        // Validate pickup points
        pickupPoints.forEach((point) => {
            const error = validatePickupPoint(point);
            if (error) {
                newPickupErrors[point.uniqueId] = error;
                hasErrors = true;
            }
        });

        // Validate drop points
        dropPoints.forEach((point) => {
            const error = validateDropPoint(point);
            if (error) {
                newDropErrors[point.uniqueId] = error;
                hasErrors = true;
            }
        });

        setPickupErrors(newPickupErrors);
        setDropErrors(newDropErrors);
        return !hasErrors;
    };

    // Clear error for specific point
    const clearPickupError = (uniqueId) => {
        setPickupErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[uniqueId];
            return newErrors;
        });
    };

    const clearDropError = (uniqueId) => {
        setDropErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[uniqueId];
            return newErrors;
        });
    };

    // Get all selected pickup locations (excluding current one)
    const getSelectedPickupLocations = (currentUniqueId) => {
        return pickupPoints
            .filter(point => point.uniqueId !== currentUniqueId)
            .filter(point => point.name && point.stateName)
            .map(point => `${point.name}, ${point.stateName}`);
    };

    // Get all selected drop locations (excluding current one)
    const getSelectedDropLocations = (currentUniqueId) => {
        return dropPoints
            .filter(point => point.uniqueId !== currentUniqueId)
            .filter(point => point.name && point.stateName)
            .map(point => `${point.name}, ${point.stateName}`);
    };

    // Filter out already selected locations from dropdown options
    const filterAvailableOptions = (items, selectedLocations) => {
        return items.filter(item => !selectedLocations.includes(item.label));
    };
    // Load initial city data only once when modal opens
    useEffect(() => {
        const loadInitialCities = async () => {
            if (initialCityData.isLoading || initialCityData.isLoaded) return;

            setInitialCityData(prev => ({ ...prev, isLoading: true }));

            try {
                const res = await getCityLocationList({
                    countryCode: "IN",
                    stateCode: "",
                    offset: 1,
                    limit: 20, // Load more cities initially for better UX
                    search: "",
                });

                if (res?.responseCode === 200) {
                    const list = res?.data?.all_cities.map((c) => ({
                        ...c,
                        _id: `${c.name}, ${c.stateName}`,
                        label: `${c.name}, ${c.stateName}`
                    }));

                    setInitialCityData({
                        items: list,
                        total: res?.data?.total_record,
                        isLoaded: true,
                        isLoading: false
                    });
                } else {
                    toast.error(res?.responseMessage);
                    setInitialCityData({
                        items: [],
                        total: 0,
                        isLoaded: true,
                        isLoading: false
                    });
                }
            } catch (error) {
                toast.error('Failed to load cities');
                setInitialCityData({
                    items: [],
                    total: 0,
                    isLoaded: true,
                    isLoading: false
                });
            }
        };


        loadInitialCities();
    }, []);


    const fetchFromCity = useCallback(async (search, page, limit, excludeSelected = []) => {
        const searchTerm = search?.trim() || "";


        if (!searchTerm && page === 1) {
            return {
                items: filterAvailableOptions(initialCityData.items, excludeSelected),
                total: initialCityData.total,
            };
        }


        const cacheKey = `${searchTerm}_${page}_${limit}`;

        let result;
        if (searchCacheRef.current.has(cacheKey)) {
            result = searchCacheRef.current.get(cacheKey);
        } else {

            try {
                const res = await getCityLocationList({
                    countryCode: "IN",
                    stateCode: "",
                    offset: page,
                    limit,
                    search: searchTerm,
                });

                if (res?.responseCode === 200) {
                    const list = res?.data?.all_cities.map((c) => ({
                        ...c,
                        _id: `${c.name}, ${c.stateName}`,
                        label: `${c.name}, ${c.stateName}`
                    }));

                    result = {
                        items: list,
                        total: res?.data?.total_record,
                    };


                    searchCacheRef.current.set(cacheKey, result);
                } else {
                    toast.error(res?.responseMessage);
                    return { items: [], total: 0 };
                }
            } catch (error) {
                toast.error('Failed to fetch cities');
                return { items: [], total: 0 };
            }
        }


        return {
            items: filterAvailableOptions(result.items, excludeSelected),
            total: result.total,
        };
    }, [initialCityData]);


    const handlePickupDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setPickupPoints((items) => {
                const oldIndex = items.findIndex(item => item.uniqueId === active.id);
                const newIndex = items.findIndex(item => item.uniqueId === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };


    const handleDropDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setDropPoints((items) => {
                const oldIndex = items.findIndex(item => item.uniqueId === active.id);
                const newIndex = items.findIndex(item => item.uniqueId === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const addFromInput = (e) => {

        if (!areAllPickupPointsFilled) {
            toast.error("Please fill all existing pickup locations before adding more");
            return;
        }

        setPickupPoints((preState) => {
            return [...preState, {
                ...initiFrom,
                uniqueId: generateUniqueId()
            }];
        });
    };

    const addToInput = (e) => {

        if (!areAllDropPointsFilled) {
            toast.error("Please fill all existing drop locations before adding more");
            return;
        }

        setDropPoints((preState) => {
            return [...preState, {
                ...initiTo,
                uniqueId: generateUniqueId()
            }];
        });
    };

    useEffect(() => {
        const listPick = pickupPoints.map((item) => {
            return {
                from_city: item?.name,
                from_state: item?.stateName,
                lat: item?.latitude,
                long: item?.longitude
            }
        })

        const listDrop = dropPoints.map((item) => {
            return {
                to_city: item?.name,
                to_state: item?.stateName,
                lat: item?.latitude,
                long: item?.longitude
            }
        })

        setFromLoction(listPick);
        setToLoction(listDrop);
    }, [pickupPoints, dropPoints])

    const allStops = useMemo(() => {
        const formatFromLabel = (point, icon) => ({
            ...point,
            label: `${icon} ${point.from_city}, ${point.from_state}`,
        });

        const formatToLabel = (point, icon) => ({
            ...point,
            label: `${icon} ${point.to_city}, ${point.to_state}`,
        });

        const formattedPickups = pickupPoints.map(point => formatFromLabel(point, "📤"));
        const formattedDrops = dropPoints.map(point => formatToLabel(point, "📥"));

        return [...formattedPickups, ...formattedDrops];
    }, [pickupPoints, dropPoints]);

    const origin = useMemo(
        () => new window.google.maps.LatLng(allStops[0]?.lat, allStops[0]?.long),
        [allStops]
    );
    const destination = useMemo(
        () =>
            new window.google.maps.LatLng(
                allStops[allStops.length - 1]?.lat,
                allStops[allStops.length - 1]?.long
            ),
        [allStops]
    );

    const waypoints = useMemo(
        () =>
            allStops.slice(1, -1).map((stop) => ({
                location: new window.google.maps.LatLng(stop?.lat, stop?.long),
                stopover: true,
            })),
        [allStops]
    );



    const directionsOptions = useMemo(() => {
        return directionsResponse
            ? {
                directions: directionsResponse,
                polylineOptions: {
                    strokeColor: "#62c412",
                    strokeWeight: 6,
                    strokeOpacity: 0.9,
                },
                markerOptions: { visible: false }, // Hide default markers
            }
            : {};
    }, [directionsResponse]);

    useEffect(() => {
        if (!isLoaded) return;

        const calculateRoute = async () => {
            try {
                const directionsService = new window.google.maps.DirectionsService();
                const result = await directionsService.route({
                    origin,
                    destination,
                    waypoints,
                    travelMode: window.google.maps.TravelMode.DRIVING,
                    optimizeWaypoints: false,
                });

                setDirectionsResponse(result);
                //   setError("");

                // Fit map to all stops
                const bounds = new window.google.maps.LatLngBounds();
                allStops.forEach((stop) => {
                    bounds.extend(new window.google.maps.LatLng(stop.lat, stop.long));
                });
                if (mapRef.current) {
                    mapRef.current.fitBounds(bounds);
                }
            } catch (err) {
                //   setError("Error loading route: " + err.message);
                console.error(err);
            }
        };

        calculateRoute();
    }, [isLoaded, origin, destination, waypoints, allStops]);

    const onSubmitTrip = () => {

        if (!validateAllPoints()) {

            return;
        }
        submitByWithoutLr();
    }

    useEffect(() => {
        return () => {
            searchCacheRef.current.clear();
        };
    }, []);

    const modifyProps = {
        ...props,
        onSubmit: onSubmitTrip,
        isLoadding: loading,
        disabled: loading,
        modalZIndex
    }

    const removeFromLocation = (index) => {
        let modify = [...pickupPoints]
        const removedItem = modify[index];
        modify.splice(index, 1)
        setPickupPoints(modify)
        clearPickupError(removedItem.uniqueId);
    }

    const removeToLocation = (index) => {
        let modify = [...dropPoints]
        const removedItem = modify[index];
        modify.splice(index, 1)
        setDropPoints(modify)
        clearDropError(removedItem.uniqueId);
    }

    if (!initialCityData.isLoaded) {
        return (
            <TripRoutingModal {...modifyProps}>
                <div className='__mapFlxr_bx'>
                    <div className='__lftsIde_bx'><div className="loaderBg" style={{ textAlign: "center",paddingRight:"5rem" }}><Loading /></div></div>
                    <div className='__rtsIde_bx'>
                        <div className='__rtsIde_main'>
                            <div style={{
                                textAlign: 'center',
                                padding: '50px 20px',
                                fontSize: '16px',
                                color: '#666'
                            }}>
                                {initialCityData.isLoading ? 'Loading cities...' : 'Preparing city data...'}
                            </div>
                        </div>
                        <div className='__rtsIde_footer'>
                            <button className='anime-btn' disabled>
                                Loading...
                            </button>
                        </div>
                    </div>
                </div>
            </TripRoutingModal>
        );
    }

    const addNewItemHandler = (e, type, index1, index) => {

        setOpenLoction(true)
        setTypeOfDropdown((i) => ({
            ...i,
            type,
            index1,
            index
        }))

        setModalZIndex(999)
    }

    const callBackDataMap = async (data) => {
        if (!typeOfDropdown?.type || typeof typeOfDropdown.index1 !== 'number') return;

        const { type, index1 } = typeOfDropdown;

        // Format the selected city data
        const formattedData = {
            ...data,
            _id: data.name, // or use a better unique ID if available
            label: `${data.name}, ${data.stateName}`,
        };

        try {
            if (type === "from") {
                // Assuming you have a state like `pickPoints`
                setPickupPoints((prev) =>
                    prev.map((point, idx) =>
                        idx === index1
                            ? {
                                ...point,
                                to_city: data.name || '',
                                to_state: data.stateName || '',
                                lat: data.latitude || '',
                                long: data.longitude || '',
                                ...formattedData,
                            }
                            : point
                    )
                );

                // Clear any error for this item if valid
                if (data.name && data.stateName && data.latitude && data.longitude) {
                    clearPickupError?.(prev => prev.filter((_, idx) => idx !== index1)); // adjust based on your error structure
                }
            }

            if (type === "to") {
                setDropPoints((prev) =>
                    prev.map((point, idx) =>
                        idx === index1
                            ? {
                                ...point,
                                to_city: data.name || '',
                                to_state: data.stateName || '',
                                lat: data.latitude || '',
                                long: data.longitude || '',
                                ...formattedData,
                            }
                            : point
                    )
                );

                // Clear error for this drop point
                if (data.name && data.stateName && data.latitude && data.longitude) {
                    // clearDropError(point.uniqueId); // assuming clearDropError takes uniqueId
                    clearDropError?.(prev => prev.filter((_, idx) => idx !== index1));
                }
            }

            // Optional: close modal or reset type
            setOpenLoction(false);
            setTypeOfDropdown({ type: '', index1: null, index: null });
            setModalZIndex("")
        } catch (err) {
            console.error('Error updating location:', err);
        }
    };
    let gmapLocate = {
        isOpen: openLoction,
        closeModal: (e) => {
            setOpenLoction(false)
        setModalZIndex("")
        },
        modalMainClass: "_dashCommon_Modal _mdModalLoction",
        modalTitle: 'Add Location',
        submitTitle: 'Add Location',
        callBackDataMap: callBackDataMap
    };


    return (
        <>
            <TripRoutingModal {...modifyProps}>
                <div className='__mapFlxr_bx'>
                    <div className='__lftsIde_bx'>
                        {!isLoaded ? (
                           <div className="loaderBg" style={{ textAlign: "center" }}><Loading /></div>
                        ) : (
                            <GoogleMap
                                mapContainerStyle={{ width: "100%", height: "100%" }}
                                center={defaultCenter}
                                zoom={defaultZoom}
                                options={mapOptions}
                                onLoad={(map) => {
                                    mapRef.current = map;
                                }}
                            >
                                {/* Render Markers */}
                                {allStops.map((stop, index) => {
                                    let icon = null;
                                    let label = null;

                                    // First stop: Truck icon (origin)
                                    if (index === 0) {
                                        icon = {
                                            url: "https://img.icons8.com/color/48/000000/truck.png",
                                            scaledSize: new window.google.maps.Size(40, 40),
                                            origin: new window.google.maps.Point(0, 0),
                                            anchor: new window.google.maps.Point(20, 40),
                                        };
                                    }
                                    // Last stop: Warehouse icon (destination)
                                    else if (index === allStops.length - 1) {
                                        icon = {
                                            url: "https://img.icons8.com/fluency/48/000000/warehouse.png",
                                            scaledSize: new window.google.maps.Size(40, 40),
                                            origin: new window.google.maps.Point(0, 0),
                                            anchor: new window.google.maps.Point(20, 40),
                                        };
                                    }
                                    // Intermediate stops: Number label
                                    else {
                                        label = {
                                            text: `${index}`,
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                            color: "white",
                                            backgroundColor: index < pickupPoints.length ? "#3366FF" : "#FF5722",
                                            borderRadius: "50%",
                                            padding: "0",
                                            textAlign: "center",
                                            width: "20px",
                                            height: "20px",
                                            border: "2px solid white",
                                        };
                                    }

                                    return (
                                        <Marker
                                            key={stop._id}
                                            position={{
                                                lat: parseFloat(stop.lat),
                                                lng: parseFloat(stop.long)
                                            }}
                                            icon={icon}
                                            label={label}
                                            title={stop.label}
                                        />
                                    );
                                })}

                                {/* Render Route */}
                                {directionsResponse && (
                                    <DirectionsRenderer options={directionsOptions} />
                                )}
                            </GoogleMap>

                        )}
                    </div>

                    <div className='__rtsIde_bx'>
                        <div className='__rtsIde_main'>
                            <div className='__mngL_bx'>
                                <div className='__mngInnr_bx'>
                                    <h4>From Location</h4>

                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handlePickupDragEnd}
                                        modifiers={[restrictToVerticalAxis]}
                                    >
                                        <SortableContext
                                            items={pickupPoints.map(item => item.uniqueId)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {pickupPoints.map((item, index) => (
                                                <SortableDropdownItem
                                                    key={item.uniqueId}
                                                    id={item.uniqueId}
                                                >
                                                    <div>
                                                        <div style={{ display: 'flex', justifyContent: "center", alignItems: "center", gap: "10px" }}>
                                                            <RouteCustomDropdown
                                                              label = {`Select From Location (${index+1})`}
                                                                placeholder="Select first pickup city"
                                                                fetchData={(search, page, limit) => {
                                                                    // Get all selected locations except current one
                                                                    const selectedPickups = getSelectedPickupLocations(item.uniqueId);
                                                                    const selectedDrops = getSelectedDropLocations();
                                                                    const allSelected = [...selectedPickups];

                                                                    return fetchFromCity(search, page, limit, allSelected);
                                                                }}
                                                                onChange={(data) => {
                                                                    setPickupPoints(prevPoints =>
                                                                        prevPoints.map(point =>
                                                                            point.uniqueId === item.uniqueId
                                                                                ? {

                                                                                    uniqueId: point.uniqueId,
                                                                                    from_city: data.name || '',
                                                                                    from_state: data.stateName || '',
                                                                                    lat: data.latitude || '',
                                                                                    long: data.longitude || '',

                                                                                    ...data
                                                                                }
                                                                                : point
                                                                        )
                                                                    );

                                                                    if (data.name && data.stateName && data.latitude && data.longitude) {
                                                                        clearPickupError(item.uniqueId);
                                                                    }
                                                                }}
                                                                selectedItem={item}
                                                                pickUpPoint={true}
                                                                error={pickupErrors[item.uniqueId]}
                                                                errorMessage={pickupErrors[item.uniqueId]}
                                                                isAddButton={true}
                                                                addButtonAction={(e) => addNewItemHandler(e, "from", index, item)}
                                                            />
                                                            {pickupPoints?.length > 1 &&
                                                                <CrossButton
                                                                    className={'_switchBtn  __animEffect_btn'}
                                                                    onClick={() => removeFromLocation(index)}
                                                                >
                                                                    <svg
                                                                        className="cross"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="13"
                                                                        height="13"
                                                                        viewBox="0 0 13 13"
                                                                        fill="none"
                                                                    >
                                                                        <path
                                                                            d="M12.5321 0.477671C12.4422 0.387555 12.3353 0.316061 12.2177 0.267281C12.1001 0.2185 11.9741 0.193391 11.8468 0.193391C11.7195 0.193391 11.5934 0.2185 11.4758 0.267281C11.3582 0.316061 11.2514 0.387555 11.1615 0.477671L6.40798 5.22143L1.6545 0.46795C1.56451 0.377952 1.45766 0.306563 1.34008 0.257857C1.22249 0.20915 1.09646 0.184082 0.969185 0.184082C0.84191 0.184082 0.715881 0.20915 0.598294 0.257857C0.480707 0.306563 0.373865 0.377952 0.283868 0.46795C0.19387 0.557947 0.122481 0.664789 0.0737746 0.782376C0.0250685 0.899963 -9.48274e-10 1.02599 0 1.15327C9.48275e-10 1.28054 0.0250684 1.40657 0.0737746 1.52416C0.122481 1.64175 0.19387 1.74859 0.283868 1.83858L5.03735 6.59206L0.283868 11.3455C0.19387 11.4355 0.122481 11.5424 0.0737746 11.66C0.0250685 11.7776 0 11.9036 0 12.0309C0 12.1581 0.0250685 12.2842 0.0737746 12.4017C0.122481 12.5193 0.19387 12.6262 0.283868 12.7162C0.373865 12.8062 0.480707 12.8776 0.598294 12.9263C0.715881 12.975 0.84191 13 0.969185 13C1.09646 13 1.22249 12.975 1.34008 12.9263C1.45766 12.8776 1.56451 12.8062 1.6545 12.7162L6.40798 7.9627L11.1615 12.7162C11.2515 12.8062 11.3583 12.8776 11.4759 12.9263C11.5935 12.975 11.7195 13 11.8468 13C11.974 13 12.1001 12.975 12.2177 12.9263C12.3353 12.8776 12.4421 12.8062 12.5321 12.7162C12.6221 12.6262 12.6935 12.5193 12.7422 12.4017C12.7909 12.2842 12.816 12.1581 12.816 12.0309C12.816 11.9036 12.7909 11.7776 12.7422 11.66C12.6935 11.5424 12.6221 11.4355 12.5321 11.3455L7.77861 6.59206L12.5321 1.83858C12.9015 1.46919 12.9015 0.847062 12.5321 0.477671Z"
                                                                            fill="black"
                                                                        />
                                                                    </svg>
                                                                </CrossButton>
                                                            }
                                                        </div>

                                                    </div>
                                                </SortableDropdownItem>
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                    <button
                                        style={{
                                            fontSize: "18px",
                                            background: "none",
                                            textAlign: "left",
                                            cursor: areAllPickupPointsFilled ? "pointer" : "not-allowed",
                                            width: "170px",
                                            opacity: areAllPickupPointsFilled ? 1 : 0.5,
                                            color: areAllPickupPointsFilled ? "inherit" : "#999"
                                        }}
                                        onClick={addFromInput}
                                        disabled={!areAllPickupPointsFilled}
                                    >
                                        + Add From Location
                                    </button>
                                </div>

                                <div className='__mngInnr_bx'>
                                    <h4>To Location</h4>

                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDropDragEnd}
                                        modifiers={[restrictToVerticalAxis]}
                                    >
                                        <SortableContext
                                            items={dropPoints.map(item => item.uniqueId)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {dropPoints.map((item, index) => (
                                                <SortableDropdownItem
                                                    key={item.uniqueId}
                                                    id={item.uniqueId}
                                                >
                                                    <div>
                                                        <div style={{ display: 'flex', justifyContent: "center", alignItems: "center", gap: "10px" }}>
                                                            <RouteCustomDropdown
                                                             label = {`Select To Location (${index+1})`}
                                                                placeholder="Select first drop city"
                                                                fetchData={(search, page, limit) => {

                                                                    const selectedPickups = getSelectedPickupLocations();
                                                                    const selectedDrops = getSelectedDropLocations(item.uniqueId);
                                                                    const allSelected = [...selectedDrops];

                                                                    return fetchFromCity(search, page, limit, allSelected);
                                                                }}
                                                                onChange={(data) => {
                                                                    setDropPoints(prevPoints =>
                                                                        prevPoints.map(point =>
                                                                            point.uniqueId === item.uniqueId
                                                                                ? {
                                                                                    uniqueId: point.uniqueId,
                                                                                    to_city: data.name || '',
                                                                                    to_state: data.stateName || '',
                                                                                    lat: data.latitude || '',
                                                                                    long: data.longitude || '',
                                                                                    ...data
                                                                                }
                                                                                : point
                                                                        )
                                                                    );

                                                                    if (data.name && data.stateName && data.latitude && data.longitude) {
                                                                        clearDropError(item.uniqueId);
                                                                    }
                                                                }}
                                                                selectedItem={item}
                                                                dropPoint={true}
                                                                error={dropErrors[item.uniqueId]}
                                                                errorMessage={dropErrors[item.uniqueId]}
                                                                isAddButton={true}
                                                                addButtonAction={(e) => addNewItemHandler(e, "to", index, item)}
                                                            />
                                                            {dropPoints?.length > 1 &&
                                                                <CrossButton
                                                                    className={'_switchBtn  __animEffect_btn'}
                                                                    onClick={() => removeToLocation(index)}
                                                                >
                                                                    <svg
                                                                        className="cross"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="13"
                                                                        height="13"
                                                                        viewBox="0 0 13 13"
                                                                        fill="none"
                                                                    >
                                                                        <path
                                                                            d="M12.5321 0.477671C12.4422 0.387555 12.3353 0.316061 12.2177 0.267281C12.1001 0.2185 11.9741 0.193391 11.8468 0.193391C11.7195 0.193391 11.5934 0.2185 11.4758 0.267281C11.3582 0.316061 11.2514 0.387555 11.1615 0.477671L6.40798 5.22143L1.6545 0.46795C1.56451 0.377952 1.45766 0.306563 1.34008 0.257857C1.22249 0.20915 1.09646 0.184082 0.969185 0.184082C0.84191 0.184082 0.715881 0.20915 0.598294 0.257857C0.480707 0.306563 0.373865 0.377952 0.283868 0.46795C0.19387 0.557947 0.122481 0.664789 0.0737746 0.782376C0.0250685 0.899963 -9.48274e-10 1.02599 0 1.15327C9.48275e-10 1.28054 0.0250684 1.40657 0.0737746 1.52416C0.122481 1.64175 0.19387 1.74859 0.283868 1.83858L5.03735 6.59206L0.283868 11.3455C0.19387 11.4355 0.122481 11.5424 0.0737746 11.66C0.0250685 11.7776 0 11.9036 0 12.0309C0 12.1581 0.0250685 12.2842 0.0737746 12.4017C0.122481 12.5193 0.19387 12.6262 0.283868 12.7162C0.373865 12.8062 0.480707 12.8776 0.598294 12.9263C0.715881 12.975 0.84191 13 0.969185 13C1.09646 13 1.22249 12.975 1.34008 12.9263C1.45766 12.8776 1.56451 12.8062 1.6545 12.7162L6.40798 7.9627L11.1615 12.7162C11.2515 12.8062 11.3583 12.8776 11.4759 12.9263C11.5935 12.975 11.7195 13 11.8468 13C11.974 13 12.1001 12.975 12.2177 12.9263C12.3353 12.8776 12.4421 12.8062 12.5321 12.7162C12.6221 12.6262 12.6935 12.5193 12.7422 12.4017C12.7909 12.2842 12.816 12.1581 12.816 12.0309C12.816 11.9036 12.7909 11.7776 12.7422 11.66C12.6935 11.5424 12.6221 11.4355 12.5321 11.3455L7.77861 6.59206L12.5321 1.83858C12.9015 1.46919 12.9015 0.847062 12.5321 0.477671Z"
                                                                            fill="black"
                                                                        />
                                                                    </svg>
                                                                </CrossButton>
                                                            }
                                                        </div>

                                                    </div>
                                                </SortableDropdownItem>
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                    <button
                                        style={{
                                            fontSize: "18px",
                                            background: "none",
                                            textAlign: "left",
                                            cursor: areAllDropPointsFilled ? "pointer" : "not-allowed",
                                            width: "170px",
                                            opacity: areAllDropPointsFilled ? 1 : 0.5,
                                            color: areAllDropPointsFilled ? "inherit" : "#999"
                                        }}
                                        onClick={addToInput}
                                        disabled={!areAllDropPointsFilled}
                                    >
                                        + Add To Location
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className='__rtsIde_footer'>
                            <button className='anime-btn' onClick={onSubmitTrip} disabled={loading}>{loading ? "Creating..." : "Create Trip"}</button>
                        </div>
                    </div>
                </div>
            </TripRoutingModal>
            {openLoction && <AddLocationModal {...gmapLocate} />}
           {/* {openLoction &&  <div className={openLoction ? "offBack_drop" : ""}></div>} */}
            <style jsx>{`
                .dragging {
                    z-index: 999;
                    cursor: grabbing !important;
                }
            `}</style>
        </>
    )
}

export default RoutePanningWithoutLrModal